<script setup>
import { inject, ref, watch, nextTick, onBeforeMount } from 'vue';

import axios from '@/axios';
import { collectionV2 } from '@/composables/resource';
import { debounce } from '@/utils';

import SearchViewQueryItem from './SearchViewQueryItem.vue';

const search = inject('app-search', null);

if (!search) {
    throw new Error('app-search-view must be used within an app-search component');
}

const input = ref(null);
const inputEl = ref(null);

const props = defineProps({
    placeholder: {
        type: String,
    },
});

const history = collectionV2({
    url: '/api/searches',
    params: {
        filters: {
            context: search.state.context,
        }
    }
});

const trending = collectionV2({
    url: '/api/trending-searches',
    params: {
        filters: {
            context: search.state.context,
        }
    }
});

const suggestionQuery = ref(null);

const triggerSuggestions = (query) => {
    suggestionQuery.value = query;
    trending.fetch({
        params: {
            query,
            context: search.state.context,
        }
    });
}

const delayedTrigger = debounce(triggerSuggestions);

const clearSuggestions = () => {
    suggestionQuery.value = null;
    trending.fetch();
}

const deleteHistory = (id) => {
    axios.delete(`/api/searches/${id}`).then(() => {
        history.fetch();
    }).catch((err) => {
        console.error(err);
    });
}

const setOpen = (value) => {
    search.state.open = value;
}

const clearQuery = () => {
    input.value = '';
    clearSuggestions();

    nextTick(() => {
        inputEl.value.focus();
    });
}

const setQuery = (query) => {
    input.value = query;

    nextTick(() => {
        inputEl.value.focus();
    });
}

const onInput = (e) => {
    if (!e.target.value) {
        clearSuggestions();
    } else {
        delayedTrigger(e.target.value);
    }
}

const onSubmit = () => {
    submit(input.value);
}

const submit = (query) => {
    setOpen(false);

    search.submit(query);

    if (search.state.context && query) {
        axios.post('/api/searches', {
            query,
            context: search.state.context,
        }).catch((err) => {
            console.error(err);
        });
    }
}

watch(() => search.state.open , (opened) => {
    if (opened) {
        input.value = search.state.query ?? '';
        nextTick(() => {
            inputEl.value?.focus();
        });
        history.fetch();
        trending.fetch();
    } else {
        suggestionQuery.value = null;
    }
}, { immediate: true });
</script>

<template>
    <div v-if="search.state.open">
        <app-dialog :open="search.state.open" @update:open="setOpen($event)" full-screen min-height="md" max-width>
            <app-dialog-header no-padding>
                <form class="flex items-center w-full h-[72px]" @submit.prevent="onSubmit">
                    <app-icon-button @click="setOpen(false)" icon="arrow-back" aria-label="Close search view" />
                    <div class="flex items-center grow min-w-0">
                        <input
                            @input="onInput"
                            v-model="input"
                            ref="inputEl"
                            type="text"
                            class="block outline-none border-none focus:ring-0 p-0 m-0 bg-white text-white-foreground w-full"
                            :placeholder="search.state.placeholder"
                        />
                    </div>
                    <app-icon-button v-show="input" icon="cancel" aria-label="Clear search" @click="clearQuery" />
                </form>
            </app-dialog-header>
            <app-dialog-body :padding-x="false">
                <div class="flex flex-col">
                    <template v-if="suggestionQuery">
                        <SearchViewQueryItem
                            :query="suggestionQuery"
                            leading-icon="search"
                            @submit="submit(suggestionQuery)"
                            hide-set-query />
                    </template>
                    <template v-else>
                        <SearchViewQueryItem
                            v-for="item in history.data"
                            :key="item.id"
                            :query="item.query"
                            leading-icon="history"
                            @submit="submit(item.query)"
                            @remove="deleteHistory(item.id)"
                            show-remove
                            @set-query="setQuery(item.query)" />
                    </template>

                    <SearchViewQueryItem
                        v-for="item in trending.data"
                        :key="item.id"
                        :query="item.query"
                        leading-icon="trending-up"
                        @submit="submit(item.query)"
                        @set-query="setQuery(item.query)" />
                </div>
            </app-dialog-body>
        </app-dialog>
    </div>
</template>
